import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiError, formatAR, parseAR } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useBranding } from "@/context/BrandingContext";
import { BrandLogo } from "@/components/AppLayout";
import { DateField } from "@/components/DatePickers";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plug, Save, Download, Plus, Trash2, LogOut, ArrowLeft, Eye } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import MemoriaCalculoTab from "./admin/MemoriaCalculoTab";
import SueldosTab from "./admin/SueldosTab";
import TycTab from "./admin/TycTab";

export default function AdminPage() {
  const nav = useNavigate();
  const { logout } = useAuth();

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <header className="bg-white border-b border-border px-6 py-4 flex items-center justify-between sticky top-0 z-10">
        <div className="flex items-center gap-4">
          <BrandLogo />
          <span className="label-tiny border-l border-border pl-4">Panel Admin</span>
        </div>
        <div className="flex gap-2">
          <Link to="/app"><Button variant="ghost" className="rounded-none gap-2"><ArrowLeft size={15} /> App</Button></Link>
          <Button variant="ghost" onClick={() => { logout(); nav("/"); }} data-testid="admin-logout" className="rounded-none gap-2 text-destructive"><LogOut size={15} /> Salir</Button>
        </div>
      </header>

      <div className="max-w-5xl mx-auto p-6">
        <Tabs defaultValue="branding">
          <TabsList className="rounded-none bg-white border border-border p-0 h-auto flex-wrap">
            {[["branding", "Branding"], ["landing", "Landing"], ["smtp", "SMTP"], ["params", "Parámetros"], ["sueldos", "Sueldos"], ["memoria", "Memoria de cálculo"], ["feedback", "Feedback"], ["legis", "Legislación"], ["leads", "Leads & Uso"], ["tyc", "Términos y Privacidad"]].map(([v, l]) => (
              <TabsTrigger key={v} value={v} data-testid={`tab-${v}`} className="rounded-none data-[state=active]:bg-slate-900 data-[state=active]:text-white text-slate-600 px-5 py-2.5">{l}</TabsTrigger>
            ))}
          </TabsList>
          <div className="mt-6">
            <TabsContent value="branding"><BrandingTab /></TabsContent>
            <TabsContent value="landing"><LandingTab /></TabsContent>
            <TabsContent value="smtp"><SmtpTab /></TabsContent>
            <TabsContent value="params"><ParamsTab /><div className="mt-6"><Seccion6Panel /></div></TabsContent>
            <TabsContent value="sueldos"><SueldosTab /></TabsContent>
            <TabsContent value="memoria"><MemoriaCalculoTab /></TabsContent>
            <TabsContent value="feedback"><FeedbackTab /></TabsContent>
            <TabsContent value="legis"><LegisTab /></TabsContent>
            <TabsContent value="leads"><LeadsTab /></TabsContent>
            <TabsContent value="tyc"><TycTab /></TabsContent>
          </div>
        </Tabs>
      </div>
    </div>
  );
}

function Panel({ title, children, actions }) {
  return (
    <div className="bg-white border border-border">
      <div className="p-5 border-b border-border flex items-center justify-between">
        <p className="label-tiny">{title}</p>{actions}
      </div>
      <div className="p-5">{children}</div>
    </div>
  );
}

function BrandingTab() {
  const { refresh } = useBranding();
  const [f, setF] = useState(null);
  useEffect(() => { api.get("/admin/branding").then((r) => setF(r.data)).catch(() => {}); }, []);
  if (!f) return null;
  const save = async () => {
    try { await api.put("/admin/branding", f); refresh(); toast.success("Branding actualizado"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const onLogoFile = (key) => (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 400 * 1024) { toast.error("El logo debe pesar menos de 400 KB"); return; }
    const reader = new FileReader();
    reader.onload = () => setF((prev) => ({ ...prev, [key]: reader.result }));
    reader.readAsDataURL(file);
  };
  const onFaviconFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const okTypes = ["image/x-icon", "image/vnd.microsoft.icon", "image/png", "image/svg+xml"];
    if (!okTypes.includes(file.type) && !file.name.toLowerCase().endsWith(".ico")) { toast.error("Formato inválido: usá .ico, .png o .svg"); return; }
    if (file.size > 300 * 1024) { toast.error("El favicon debe pesar menos de 300 KB"); return; }
    const reader = new FileReader();
    reader.onload = () => setF((prev) => ({ ...prev, favicon: reader.result }));
    reader.readAsDataURL(file);
  };
  return (
    <Panel title="Identidad visual" actions={<Button onClick={save} data-testid="branding-save" className="rounded-none brand-bg text-white gap-2"><Save size={15} /> Guardar</Button>}>
      <div className="grid md:grid-cols-2 gap-6">
        <div className="space-y-4">
          <div><Label className="label-tiny">Nombre de la app</Label><Input data-testid="brand-name" className="mt-1.5 rounded-none" value={f.appName} onChange={(e) => setF({ ...f, appName: e.target.value })} /></div>
          <div><Label className="label-tiny">Subtítulo (bajada del logo)</Label><Input data-testid="brand-tagline" className="mt-1.5 rounded-none" placeholder="Desarrollamos Software." value={f.tagline || ""} onChange={(e) => setF({ ...f, tagline: e.target.value })} /></div>
          <div>
            <Label className="label-tiny">Logo claro — para fondo claro (app y footer)</Label>
            <div className="flex gap-2 mt-1.5">
              <Input data-testid="brand-logo-light" className="rounded-none" placeholder="URL o subí un archivo →" value={f.logoLight || ""} onChange={(e) => setF({ ...f, logoLight: e.target.value })} />
              <label className="shrink-0 inline-flex items-center px-3 border border-border cursor-pointer text-sm bg-slate-50 hover:bg-slate-100">
                Subir
                <input type="file" accept="image/*" className="hidden" data-testid="brand-logo-light-upload" onChange={onLogoFile("logoLight")} />
              </label>
              {f.logoLight && <button type="button" onClick={() => setF({ ...f, logoLight: "" })} data-testid="brand-logo-light-clear" className="shrink-0 px-3 border border-border text-sm text-destructive hover:bg-slate-50">Quitar</button>}
            </div>
            <p className="text-xs text-muted-foreground mt-1">Logo oscuro / de color, para usar sobre fondos blancos (dentro de la app).</p>
          </div>
          <div>
            <Label className="label-tiny">Logo oscuro — para fondo oscuro (landing)</Label>
            <div className="flex gap-2 mt-1.5">
              <Input data-testid="brand-logo-dark" className="rounded-none" placeholder="URL o subí un archivo →" value={f.logoDark || ""} onChange={(e) => setF({ ...f, logoDark: e.target.value })} />
              <label className="shrink-0 inline-flex items-center px-3 border border-border cursor-pointer text-sm bg-slate-50 hover:bg-slate-100">
                Subir
                <input type="file" accept="image/*" className="hidden" data-testid="brand-logo-dark-upload" onChange={onLogoFile("logoDark")} />
              </label>
              {f.logoDark && <button type="button" onClick={() => setF({ ...f, logoDark: "" })} data-testid="brand-logo-dark-clear" className="shrink-0 px-3 border border-border text-sm text-destructive hover:bg-slate-50">Quitar</button>}
            </div>
            <p className="text-xs text-muted-foreground mt-1">Logo blanco / claro, para usar sobre el fondo azul del landing.</p>
          </div>
          <div><Label className="label-tiny">Texto del pie de página (footer)</Label><Input data-testid="brand-footer" className="mt-1.5 rounded-none" value={f.footerText || ""} onChange={(e) => setF({ ...f, footerText: e.target.value })} /></div>
          <div>
            <Label className="label-tiny">Favicon (ícono de la pestaña del navegador)</Label>
            <div className="flex gap-2 mt-1.5 items-center">
              <label className="shrink-0 inline-flex items-center px-3 py-2 border border-border cursor-pointer text-sm bg-slate-50 hover:bg-slate-100">
                Subir favicon
                <input type="file" accept=".ico,image/x-icon,image/vnd.microsoft.icon,image/png,image/svg+xml" className="hidden" data-testid="branding-favicon-input" onChange={onFaviconFile} />
              </label>
              {f.favicon && <img src={f.favicon} alt="favicon" data-testid="branding-favicon-preview" className="h-8 w-8 border border-border object-contain bg-white p-0.5" />}
              {f.favicon && <button type="button" onClick={() => setF({ ...f, favicon: "" })} data-testid="branding-favicon-remove" className="shrink-0 px-3 py-2 border border-border text-sm text-destructive hover:bg-slate-50">Quitar</button>}
            </div>
            <p className="text-xs text-muted-foreground mt-1">Formatos: .ico, .png o .svg — máx. 300 KB. Se muestra en la pestaña del navegador junto al título.</p>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div><Label className="label-tiny">Color primario</Label><div className="flex gap-2 mt-1.5"><input type="color" className="h-10 w-12 border border-border" value={f.primaryColor} onChange={(e) => setF({ ...f, primaryColor: e.target.value })} /><Input className="rounded-none" value={f.primaryColor} onChange={(e) => setF({ ...f, primaryColor: e.target.value })} /></div></div>
            <div><Label className="label-tiny">Color secundario</Label><div className="flex gap-2 mt-1.5"><input type="color" className="h-10 w-12 border border-border" value={f.secondaryColor} onChange={(e) => setF({ ...f, secondaryColor: e.target.value })} /><Input className="rounded-none" value={f.secondaryColor} onChange={(e) => setF({ ...f, secondaryColor: e.target.value })} /></div></div>
          </div>
        </div>
        <div className="border border-border p-6">
          <p className="label-tiny mb-2">App / footer (fondo claro)</p>
          <div className="flex items-center gap-2.5 bg-white border border-border p-3" data-testid="brand-preview-light">
            {f.logoLight ? <img src={f.logoLight} alt="" className="h-9 w-auto max-w-[120px] object-contain" /> : <span className="inline-block rounded-md" style={{ width: 12, height: 12, background: f.secondaryColor }} />}
            <div className="leading-none">
              <span className="font-head text-xl font-extrabold tracking-tight" style={{ color: f.primaryColor }}>{f.appName}</span>
              {f.tagline ? <span className="block text-[0.5rem] font-semibold tracking-[0.22em] uppercase mt-0.5 text-slate-500">{f.tagline}</span> : null}
            </div>
          </div>
          <p className="label-tiny mt-4 mb-2">Landing (fondo oscuro)</p>
          <div className="flex items-center gap-2.5 p-3" style={{ background: "#042256" }} data-testid="brand-preview-dark">
            {f.logoDark ? <img src={f.logoDark} alt="" className="h-9 w-auto max-w-[120px] object-contain" /> : <span className="inline-block rounded-md" style={{ width: 12, height: 12, background: "#00C8C8" }} />}
            <div className="leading-none">
              <span className="font-head text-xl font-extrabold tracking-tight text-white">{f.appName}</span>
              {f.tagline ? <span className="block text-[0.5rem] font-semibold tracking-[0.22em] uppercase mt-0.5 text-white/60">{f.tagline}</span> : null}
            </div>
          </div>
          <p className="mt-4 text-xs text-slate-500 border-t border-border pt-3">{f.footerText}</p>
        </div>
      </div>
    </Panel>
  );
}

function SmtpTab() {
  const [f, setF] = useState(null);
  const [apiKey, setApiKey] = useState("");
  useEffect(() => { api.get("/admin/smtp").then((r) => setF(r.data)).catch(() => {}); }, []);
  if (!f) return null;
  const save = async () => {
    try { await api.put("/admin/smtp", { provider: "brevo", senderEmail: f.senderEmail, senderName: f.senderName, brevoApiKey: apiKey || undefined }); setApiKey(""); toast.success("SMTP guardado"); api.get("/admin/smtp").then((r) => setF(r.data)).catch(() => {}); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const test = async () => {
    try { const { data } = await api.post("/admin/smtp/test"); data.ok ? toast.success(data.detail) : toast.error(data.detail); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <Panel title="Configuración de email (Brevo)" actions={
      <div className="flex gap-2">
        <Button onClick={test} variant="outline" data-testid="smtp-test" className="rounded-none gap-2"><Plug size={15} /> Probar conexión</Button>
        <Button onClick={save} data-testid="smtp-save" className="rounded-none brand-bg text-white gap-2"><Save size={15} /> Guardar</Button>
      </div>}>
      <div className="grid md:grid-cols-2 gap-4 max-w-2xl">
        <div><Label className="label-tiny">Email remitente</Label><Input data-testid="smtp-sender-email" className="mt-1.5 rounded-none" value={f.senderEmail} onChange={(e) => setF({ ...f, senderEmail: e.target.value })} /></div>
        <div><Label className="label-tiny">Nombre remitente</Label><Input data-testid="smtp-sender-name" className="mt-1.5 rounded-none" value={f.senderName} onChange={(e) => setF({ ...f, senderName: e.target.value })} /></div>
        <div className="md:col-span-2"><Label className="label-tiny">API Key de Brevo {f.hasApiKey && <span className="text-accent-success">(configurada)</span>}</Label><Input type="password" data-testid="smtp-api-key" placeholder={f.hasApiKey ? "•••••• (dejar vacío para mantener)" : "Pegá tu API key"} className="mt-1.5 rounded-none" value={apiKey} onChange={(e) => setApiKey(e.target.value)} /></div>
      </div>
      <p className="text-xs text-muted-foreground mt-4">Sin API key, el sistema opera en modo dev: el código OTP se muestra en pantalla en vez de enviarse por email.</p>
    </Panel>
  );
}

function ParamsTab() {
  const [p, setP] = useState(null);
  const [versions, setVersions] = useState([]);
  const [draft, setDraft] = useState(null);
  const [verVersion, setVerVersion] = useState(null);
  const [sel, setSel] = useState({});
  const [confirmDel, setConfirmDel] = useState(null);
  const load = () => { api.get("/admin/params/vigente").then((r) => setP(r.data)).catch(() => {}); api.get("/admin/params").then((r) => { setVersions(r.data); setSel({}); }).catch(() => {}); };
  useEffect(() => { load(); }, []);

  // build the editable string draft whenever p (re)loads — es-AR formatted
  useEffect(() => {
    if (!p) return;
    const re = p.ratesEmpleado, r18 = p.ratesEmpleador["18"], r20 = p.ratesEmpleador["20.40"];
    setDraft({
      baseMinima: formatAR(p.baseMinima), baseMaxima: formatAR(p.baseMaxima), detraccionMensual: formatAR(p.detraccionMensual),
      e_sipa: pctToStr(re.sipa), e_pami: pctToStr(re.pami), e_osecac: pctToStr(re.osecac), e_faecys: pctToStr(re.faecys), e_sec: pctToStr(re.sec),
      p18_sipa: pctToStr(r18.sipa), p18_inssjp: pctToStr(r18.inssjp), p18_fne: pctToStr(r18.fne), p18_af: pctToStr(r18.af),
      p20_sipa: pctToStr(r20.sipa), p20_inssjp: pctToStr(r20.inssjp), p20_fne: pctToStr(r20.fne), p20_af: pctToStr(r20.af),
      os: pctToStr(p.obraSocialEmpleador), art: pctToStr(p.art ?? 0.02), scvo: pctToStr(p.scvo ?? 0.005),
      seguroVidaFijo: formatAR(p.seguroVidaFijo ?? 0), artFijo: formatAR(p.artFijo ?? 0),
      validoDesde: p.validoDesde || todayDMY(), validoHasta: p.validoHasta || "", notas: p.notas || "",
    });
  }, [p]);

  if (!p || !draft) return null;

  const set = (k, v) => setDraft((d) => ({ ...d, [k]: v }));
  const pctv = (k) => parseAR(draft[k]) / 100;

  const save = async () => {
    try {
      const { data } = await api.post("/admin/params", {
        baseMinima: parseAR(draft.baseMinima), baseMaxima: parseAR(draft.baseMaxima), detraccionMensual: parseAR(draft.detraccionMensual),
        ratesEmpleado: { sipa: pctv("e_sipa"), pami: pctv("e_pami"), osecac: pctv("e_osecac"), faecys: pctv("e_faecys"), sec: pctv("e_sec") },
        ratesEmpleador: {
          "18": { sipa: pctv("p18_sipa"), inssjp: pctv("p18_inssjp"), fne: pctv("p18_fne"), af: pctv("p18_af") },
          "20.40": { sipa: pctv("p20_sipa"), inssjp: pctv("p20_inssjp"), fne: pctv("p20_fne"), af: pctv("p20_af") },
        },
        obraSocialEmpleador: pctv("os"), art: pctv("art"), scvo: pctv("scvo"),
        seguroVidaFijo: parseAR(draft.seguroVidaFijo), artFijo: parseAR(draft.artFijo),
        validoDesde: (draft.validoDesde || "").trim(), validoHasta: (draft.validoHasta || "").trim(), notas: draft.notas || "",
      });
      toast.success("Nueva versión de parámetros creada");
      if (data.overlapWarning) toast.warning(data.overlapWarning, { duration: 8000 });
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const MoneyF = (label, k, testid) => (
    <div key={k}><Label className="label-tiny">{label}</Label>
      <Input type="text" inputMode="decimal" data-testid={testid} className="mt-1.5 rounded-none" value={draft[k]}
        onChange={(e) => set(k, e.target.value)}
        onBlur={() => { const raw = String(draft[k] ?? "").trim(); if (raw !== "") set(k, formatAR(parseAR(raw))); }} />
    </div>
  );
  const PctF = (label, k, testid) => (
    <div key={k}><Label className="label-tiny">{label}</Label>
      <div className="mt-1.5 flex items-center gap-1">
        <Input type="text" inputMode="decimal" data-testid={testid} className="rounded-none text-right" value={draft[k]}
          onChange={(e) => set(k, e.target.value)}
          onBlur={() => { const raw = String(draft[k] ?? "").trim(); if (raw !== "") set(k, fmtPctStr(parseAR(raw))); }} />
        <span className="text-slate-500">%</span>
      </div>
    </div>
  );

  const nonVigentes = versions.filter((v) => !v.vigente);
  const selIds = nonVigentes.filter((v) => sel[v.id]).map((v) => v.id);
  const allSelected = nonVigentes.length > 0 && nonVigentes.every((v) => sel[v.id]);
  const toggleAll = (on) => { const m = {}; if (on) nonVigentes.forEach((v) => { m[v.id] = true; }); setSel(m); };
  const doDelete = async () => {
    const ids = confirmDel || [];
    try {
      if (ids.length === 1) await api.delete(`/admin/params/versions/${ids[0]}`);
      else await api.post("/admin/params/versions/delete-bulk", { ids });
      toast.success(ids.length === 1 ? "Versión eliminada del historial" : `${ids.length} versiones eliminadas del historial`);
      setConfirmDel(null); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-6">
      <Panel title={`Parámetros regulatorios (versión vigente v${p.version || 1})`} actions={<Button onClick={save} data-testid="params-save" className="rounded-none brand-bg text-white gap-2"><Save size={15} /> Guardar nueva versión</Button>}>
        <p className="text-xs text-slate-600 mb-1" data-testid="params-validez">
          Válido desde <b>{p.validoDesde || fmtFechaISO(p.created_at)}</b>{p.validoHasta ? <> · hasta <b>{p.validoHasta}</b></> : null} <span className="text-muted-foreground">(si ningún rango cubre el período liquidado, se usa la versión más cercana anterior)</span>
        </p>
        <p className="text-xs text-muted-foreground mb-4">Las alícuotas se escriben en porcentaje (ej. 1,11 = 1,11%). Los importes aceptan formato argentino (1.827,00) o punto decimal. PRE-LAUNCH: confirmar tasas con autoridad de nómina antes del go-live.</p>
        <div className="grid md:grid-cols-3 gap-4">
          {MoneyF("Base imponible mínima MENSUAL", "baseMinima", "pf-base-min")}
          {MoneyF("Base imponible máxima MENSUAL", "baseMaxima", "pf-base-max")}
          {MoneyF("Detracción Dto. 814 MENSUAL", "detraccionMensual", "pf-detraccion")}
        </div>
        <p className="label-tiny mt-6 mb-3">Aportes empleado</p>
        <div className="grid md:grid-cols-5 gap-4">
          {PctF("SIPA", "e_sipa", "pf-e-sipa")}
          {PctF("PAMI", "e_pami", "pf-e-pami")}
          {PctF("OSECAC", "e_osecac", "pf-e-osecac")}
          {PctF("FAECYS", "e_faecys", "pf-e-faecys")}
          {PctF("SEC", "e_sec", "pf-e-sec")}
        </div>
        <p className="label-tiny mt-6 mb-3">Contribuciones patronales — Tier 18%</p>
        <div className="grid md:grid-cols-4 gap-4">
          {PctF("SIPA", "p18_sipa", "pf-p18-sipa")}
          {PctF("INSSJP", "p18_inssjp", "pf-p18-inssjp")}
          {PctF("FNE", "p18_fne", "pf-p18-fne")}
          {PctF("AF", "p18_af", "pf-p18-af")}
        </div>
        <p className="label-tiny mt-6 mb-3">Contribuciones patronales — Tier 20,40%</p>
        <div className="grid md:grid-cols-4 gap-4">
          {PctF("SIPA", "p20_sipa", "pf-p20-sipa")}
          {PctF("INSSJP", "p20_inssjp", "pf-p20-inssjp")}
          {PctF("FNE", "p20_fne", "pf-p20-fne")}
          {PctF("AF", "p20_af", "pf-p20-af")}
        </div>
        <p className="label-tiny mt-6 mb-3">Seguros patronales</p>
        <div className="grid md:grid-cols-3 gap-4">
          {PctF("Obra Social empleador", "os", "pf-os")}
          {PctF("ART (Riesgos del Trabajo)", "art", "pf-art")}
          {PctF("SCVO (Seguro de vida obligatorio)", "scvo", "pf-scvo")}
        </div>
        <div className="grid md:grid-cols-3 gap-4 mt-4">
          {MoneyF("Seguro de Vida — SCVO ($ fijo — Sueldos)", "seguroVidaFijo", "pf-scvo-fijo")}
          {MoneyF("A.R.T. ($ fijo por empleado — Sueldos)", "artFijo", "pf-art-fijo")}
        </div>
        <div className="mt-6 grid md:grid-cols-3 gap-4">
          <div>
            <Label className="label-tiny">Válido desde (obligatorio)</Label>
            <div className="mt-1.5"><DateField value={draft.validoDesde} onChange={(v) => set("validoDesde", v)} testid="pf-valido-desde" className="w-full" /></div>
          </div>
          <div>
            <Label className="label-tiny">Válido hasta (opcional)</Label>
            <div className="mt-1.5 flex items-center gap-1">
              <DateField value={draft.validoHasta} onChange={(v) => set("validoHasta", v)} testid="pf-valido-hasta" className="flex-1" />
              {draft.validoHasta && (
                <Button type="button" variant="ghost" size="sm" data-testid="pf-valido-hasta-clear" className="rounded-none px-2 text-slate-500" onClick={() => set("validoHasta", "")} title="Quitar fecha">×</Button>
              )}
            </div>
          </div>
        </div>
        <div className="mt-4">
          <Label className="label-tiny">Notas</Label><Input className="mt-1.5 rounded-none" data-testid="pf-notas" value={draft.notas} onChange={(e) => set("notas", e.target.value)} />
        </div>
      </Panel>
      <Panel title="Historial de versiones">
        {nonVigentes.length > 0 && (
          <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
            <label className="flex items-center gap-2 text-xs text-slate-600 cursor-pointer">
              <input type="checkbox" data-testid="param-versions-select-all" checked={allSelected} onChange={(e) => toggleAll(e.target.checked)} />
              Seleccionar todas (excepto vigente)
            </label>
            <Button variant="destructive" size="sm" disabled={selIds.length === 0} data-testid="param-versions-delete-selected"
              className="rounded-none gap-1.5" onClick={() => setConfirmDel(selIds)}>
              <Trash2 size={13} /> Eliminar seleccionadas ({selIds.length})
            </Button>
          </div>
        )}
        <div className="space-y-1 text-sm">
          {versions.map((v) => (
            <div key={v.id} className="flex items-center gap-2 py-1.5 border-b border-border last:border-0 px-1 hover:bg-slate-50 transition-colors">
              {v.vigente ? <span className="w-[13px]" /> : (
                <input type="checkbox" data-testid={`param-version-check-${v.version}`} checked={!!sel[v.id]} onChange={(e) => setSel({ ...sel, [v.id]: e.target.checked })} />
              )}
              <button type="button" onClick={() => setVerVersion(v)} data-testid={`param-version-${v.version}`} className="flex-1 flex items-center justify-between text-left min-w-0">
                <span className="flex items-center gap-2">
                  <Eye size={13} className="text-slate-400" />
                  v{v.version} {v.vigente && <span className="brand-accent-text label-tiny ml-1">vigente</span>}
                </span>
                <span className="text-muted-foreground font-mono text-xs">
                  válido desde {v.validoDesde || fmtFechaISO(v.created_at)} · hasta {v.validoHasta || "—"}
                </span>
              </button>
              {!v.vigente && (
                <button type="button" data-testid={`param-version-del-${v.version}`} className="text-slate-400 hover:text-red-600 transition-colors p-1" onClick={() => setConfirmDel([v.id])} title="Eliminar versión">
                  <Trash2 size={14} />
                </button>
              )}
            </div>
          ))}
        </div>
        <p className="text-xs text-muted-foreground mt-3">Hacé clic en una versión para ver sus valores, corregirlos (versiones pasadas) y consultar la auditoría de cambios. La versión vigente no se puede eliminar.</p>
      </Panel>
      <VersionDialog v={verVersion} onClose={() => setVerVersion(null)} onSaved={load} />
      <Dialog open={!!confirmDel} onOpenChange={(o) => { if (!o) setConfirmDel(null); }}>
        <DialogContent className="rounded-none max-w-md" data-testid="param-delete-dialog">
          <DialogHeader>
            <DialogTitle>{confirmDel?.length === 1 ? "¿Eliminar esta versión del historial?" : `¿Eliminar ${confirmDel?.length} versiones del historial?`}</DialogTitle>
            <DialogDescription>
              Esta acción es permanente y no se puede deshacer. Los cálculos guardados y los recibos ya emitidos NO se modifican (conservan los valores con los que se generaron).
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-end gap-2">
            <Button variant="outline" className="rounded-none" data-testid="param-delete-cancel" onClick={() => setConfirmDel(null)}>Cancelar</Button>
            <Button variant="destructive" className="rounded-none gap-1.5" data-testid="param-delete-confirm" onClick={doDelete}><Trash2 size={14} /> Eliminar</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

const fmtPctStr = (n) => Number(Number(n || 0).toFixed(6)).toString().replace(".", ",");
const pctToStr = (v) => fmtPctStr(Number(v || 0) * 100);

const fmtFechaISO = (iso) => {
  if (!iso) return "—";
  const s = String(iso).slice(0, 10);
  return `${s.slice(8, 10)}/${s.slice(5, 7)}/${s.slice(0, 4)}`;
};

const todayDMY = () => {
  const d = new Date();
  return `${String(d.getDate()).padStart(2, "0")}/${String(d.getMonth() + 1).padStart(2, "0")}/${d.getFullYear()}`;
};

const fmtAuditVal = (val, tipo) => {
  if (tipo === "pct") return Number(Number(val || 0) * 100).toLocaleString("es-AR", { maximumFractionDigits: 6 }) + "%";
  if (tipo === "money") return "$ " + Number(val || 0).toLocaleString("es-AR", { minimumFractionDigits: 2 });
  return String(val ?? "") || "—";
};

const fmtAuditTs = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleString("es-AR", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
};

function VersionDialog({ v, onClose, onSaved }) {
  const [tab, setTab] = useState("valores");
  const [d, setD] = useState(null);
  useEffect(() => {
    if (!v) { setD(null); setTab("valores"); return; }
    const re = v.ratesEmpleado || {}, r18 = (v.ratesEmpleador || {})["18"] || {}, r20 = (v.ratesEmpleador || {})["20.40"] || {};
    setD({
      baseMinima: formatAR(v.baseMinima ?? 0), baseMaxima: formatAR(v.baseMaxima ?? 0), detraccionMensual: formatAR(v.detraccionMensual ?? 0),
      e_sipa: pctToStr(re.sipa), e_pami: pctToStr(re.pami), e_osecac: pctToStr(re.osecac), e_faecys: pctToStr(re.faecys), e_sec: pctToStr(re.sec),
      p18_sipa: pctToStr(r18.sipa), p18_inssjp: pctToStr(r18.inssjp), p18_fne: pctToStr(r18.fne), p18_af: pctToStr(r18.af),
      p20_sipa: pctToStr(r20.sipa), p20_inssjp: pctToStr(r20.inssjp), p20_fne: pctToStr(r20.fne), p20_af: pctToStr(r20.af),
      os: pctToStr(v.obraSocialEmpleador), art: pctToStr(v.art ?? 0.02), scvo: pctToStr(v.scvo ?? 0.005),
      seguroVidaFijo: formatAR(v.seguroVidaFijo ?? 0), artFijo: formatAR(v.artFijo ?? 0), notas: v.notas || "",
    });
  }, [v]);
  if (!v || !d) return null;

  const editable = !v.vigente;
  const pctv = (k) => parseAR(d[k]) / 100;
  const set = (k, val) => setD((prev) => ({ ...prev, [k]: val }));

  const save = async () => {
    try {
      const { data } = await api.put(`/admin/params/versions/${v.id}`, {
        baseMinima: parseAR(d.baseMinima), baseMaxima: parseAR(d.baseMaxima), detraccionMensual: parseAR(d.detraccionMensual),
        ratesEmpleado: { sipa: pctv("e_sipa"), pami: pctv("e_pami"), osecac: pctv("e_osecac"), faecys: pctv("e_faecys"), sec: pctv("e_sec") },
        ratesEmpleador: {
          "18": { sipa: pctv("p18_sipa"), inssjp: pctv("p18_inssjp"), fne: pctv("p18_fne"), af: pctv("p18_af") },
          "20.40": { sipa: pctv("p20_sipa"), inssjp: pctv("p20_inssjp"), fne: pctv("p20_fne"), af: pctv("p20_af") },
        },
        obraSocialEmpleador: pctv("os"), art: pctv("art"), scvo: pctv("scvo"),
        seguroVidaFijo: parseAR(d.seguroVidaFijo), artFijo: parseAR(d.artFijo),
        validoDesde: v.validoDesde || "", validoHasta: v.validoHasta || "", notas: d.notas || "",
      });
      toast.success(data.cambios ? `Cambios guardados y auditados (${data.cambios}). Los recibos ya emitidos no se modifican.` : "No hay cambios para guardar");
      onSaved(); onClose();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const F = (label, k, suf) => (
    <div key={k}>
      <Label className="label-tiny">{label}</Label>
      <div className="mt-1 flex items-center gap-1">
        <Input type="text" inputMode="decimal" data-testid={`pv-${k}`} disabled={!editable} className="rounded-none h-8 text-sm" value={d[k]}
          onChange={(e) => set(k, e.target.value)}
          onBlur={() => { if (k === "notas") return; const raw = String(d[k] ?? "").trim(); if (raw !== "") set(k, suf ? fmtPctStr(parseAR(raw)) : formatAR(parseAR(raw))); }} />
        {suf && <span className="text-slate-500 text-sm">{suf}</span>}
      </div>
    </div>
  );

  const auditRaw = v.auditoria && v.auditoria.length
    ? v.auditoria
    : [{ timestamp: v.created_at, admin: "", campo: "Versión creada", anterior: "", nuevo: `v${v.version}`, tipo: "text" }];
  const audit = [...auditRaw].reverse();

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="rounded-none max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="param-version-dialog">
        <DialogHeader>
          <DialogTitle>Versión v{v.version}{v.vigente ? " — vigente" : ""}</DialogTitle>
          <DialogDescription>
            Válido desde {v.validoDesde || fmtFechaISO(v.created_at)} · hasta {v.validoHasta || "—"} <span className="text-slate-400">(las fechas de validez no se pueden editar)</span>
          </DialogDescription>
        </DialogHeader>
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="rounded-none bg-slate-100 p-0 h-auto">
            <TabsTrigger value="valores" data-testid="pv-tab-valores" className="rounded-none data-[state=active]:bg-slate-900 data-[state=active]:text-white px-5 py-2">Valores</TabsTrigger>
            <TabsTrigger value="auditoria" data-testid="pv-tab-auditoria" className="rounded-none data-[state=active]:bg-slate-900 data-[state=active]:text-white px-5 py-2">Auditoría</TabsTrigger>
          </TabsList>
          <TabsContent value="valores" className="mt-4 space-y-4">
            {!editable && (
              <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 px-3 py-2" data-testid="pv-vigente-note">
                La versión vigente se modifica desde el panel principal ("Guardar nueva versión"). Solo las versiones pasadas se corrigen acá.
              </p>
            )}
            {editable && (
              <p className="text-xs text-slate-600 bg-slate-50 border border-border px-3 py-2" data-testid="pv-edit-note">
                Los recibos ya emitidos no se modifican; los nuevos cálculos de ese período usarán los valores corregidos. Cada cambio queda registrado en la Auditoría.
              </p>
            )}
            <div>
              <p className="label-tiny mb-2">Bases</p>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                {F("Base mínima", "baseMinima")}{F("Base máxima", "baseMaxima")}{F("Detracción Dto. 814", "detraccionMensual")}
              </div>
            </div>
            <div>
              <p className="label-tiny mb-2">Aportes empleado (%)</p>
              <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
                {F("SIPA", "e_sipa", "%")}{F("PAMI", "e_pami", "%")}{F("OSECAC", "e_osecac", "%")}{F("FAECYS", "e_faecys", "%")}{F("SEC", "e_sec", "%")}
              </div>
            </div>
            <div>
              <p className="label-tiny mb-2">Contribuciones patronales — Tier 18% (%)</p>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                {F("SIPA", "p18_sipa", "%")}{F("INSSJP", "p18_inssjp", "%")}{F("FNE", "p18_fne", "%")}{F("AF", "p18_af", "%")}
              </div>
            </div>
            <div>
              <p className="label-tiny mb-2">Contribuciones patronales — Tier 20,40% (%)</p>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                {F("SIPA", "p20_sipa", "%")}{F("INSSJP", "p20_inssjp", "%")}{F("FNE", "p20_fne", "%")}{F("AF", "p20_af", "%")}
              </div>
            </div>
            <div>
              <p className="label-tiny mb-2">Seguros patronales</p>
              <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
                {F("Obra Social empleador", "os", "%")}{F("ART", "art", "%")}{F("SCVO", "scvo", "%")}{F("SCVO $ fijo (Sueldos)", "seguroVidaFijo")}{F("ART $ fijo (Sueldos)", "artFijo")}
              </div>
            </div>
            <div>{F("Notas", "notas")}</div>
            {editable && (
              <div className="flex justify-end">
                <Button onClick={save} data-testid="pv-save" className="rounded-none brand-bg text-white gap-2"><Save size={15} /> Guardar cambios</Button>
              </div>
            )}
          </TabsContent>
          <TabsContent value="auditoria" className="mt-4">
            <div className="border border-border divide-y divide-border text-sm" data-testid="pv-audit-list">
              {audit.map((a, i) => (
                <div key={i} className="px-3 py-2 flex flex-wrap items-baseline gap-x-3 gap-y-0.5" data-testid={`pv-audit-row-${i}`}>
                  <span className="font-mono text-xs text-slate-500 whitespace-nowrap">{fmtAuditTs(a.timestamp)}</span>
                  <span className="font-medium text-slate-800">{a.campo}</span>
                  {a.campo === "Versión creada" ? (
                    <span className="text-slate-600">{a.nuevo}</span>
                  ) : (
                    <span className="text-slate-600 tabular">{fmtAuditVal(a.anterior, a.tipo)} <span className="text-slate-400">→</span> <b>{fmtAuditVal(a.nuevo, a.tipo)}</b></span>
                  )}
                  {a.admin && <span className="text-xs text-slate-400 ml-auto">{a.admin}</span>}
                </div>
              ))}
            </div>
            <p className="text-xs text-muted-foreground mt-2">Registro cronológico de correcciones (más reciente primero). Los recibos ya emitidos conservan sus valores históricos.</p>
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}

const S6_ITEMS = [
  ["sipa", "SIPA — Jubilación (Ley 24.241)"],
  ["inssjp", "INSSJP / PAMI (Ley 19.032)"],
  ["fne", "Fondo Nacional de Empleo (Ley 24.013)"],
  ["af", "Asignaciones Familiares (Ley 24.714)"],
  ["osecac", "Obra social — OSECAC (Ley 23.660)"],
  ["art", "A.R.T. (Ley 24.557)"],
  ["scvo", "S.C.V.O. — Seguro de vida (Dto. 1567/74)"],
];
const S6_BASE = { sipa: 246498, inssjp: 246498, fne: 246498, af: 246498, osecac: 400000, art: 246498, scvo: 246498 };
const fmtPct = (v) => (Number(v || 0) * 100).toFixed(2).replace(".", ",") + "%";
const fmtMon = (v) => "$ " + Math.round(v || 0).toLocaleString("es-AR");

function Seccion6Panel() {
  const [cfg, setCfg] = useState(null);
  const [params, setParams] = useState(null);
  const [tier, setTier] = useState("18");
  const [extraPdf, setExtraPdf] = useState(false);
  const loadParams = () => api.get("/admin/params/vigente").then((r) => setParams(r.data)).catch(() => {});
  useEffect(() => { api.get("/admin/seccion6-config").then((r) => { setCfg(r.data.items); setExtraPdf(!!r.data.extraVisiblePdf); }).catch(() => {}); loadParams(); }, []);
  if (!cfg || !params) return null;
  const setFlag = (k, field, v) => setCfg({ ...cfg, [k]: { ...cfg[k], [field]: v } });
  const save = async () => {
    try { await api.put("/admin/seccion6-config", { items: cfg, extraVisiblePdf: extraPdf }); toast.success("Sección 6 actualizada"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const c = (k) => cfg[k] || { visibleCalc: true, editable: true, visiblePdf: true };
  const tr = (params.ratesEmpleador && params.ratesEmpleador[tier]) || {};
  const RATE = { sipa: tr.sipa, inssjp: tr.inssjp, fne: tr.fne, af: tr.af, osecac: params.obraSocialEmpleador, art: params.art, scvo: params.scvo };
  const rate = (k) => Number(RATE[k] || 0);
  const imp = (k) => rate(k) * S6_BASE[k];
  const subtotal = S6_ITEMS.reduce((s, [k]) => s + imp(k), 0);
  const sacBrutoSample = 198895.03;
  const costoTotal = sacBrutoSample + subtotal;
  const sussImp = ["sipa", "inssjp", "fne", "af"].reduce((s, k) => s + imp(k), 0);
  const sussVisiblePdf = ["sipa", "inssjp", "fne", "af"].some((k) => c(k).visiblePdf);

  return (
    <Panel title="Sección 6 — Contribuciones patronales (visibilidad)" actions={<Button onClick={save} data-testid="s6-save" className="rounded-none brand-bg text-white gap-2"><Save size={15} /> Guardar</Button>}>
      <p className="text-xs text-muted-foreground mb-4">Controlá qué ítems patronales por defecto se muestran en la calculadora, si el usuario puede editar su alícuota, y si aparecen en el recibo (PDF). Aplica a los tiers 18% y 20,40%. El COSTO TOTAL siempre incluye todos los ítems (no cambia al ocultar).</p>
      <div className="grid lg:grid-cols-2 gap-6">
        {/* Tabla de toggles */}
        <table className="w-full text-sm border border-border" data-testid="s6-table">
          <thead><tr className="bg-slate-100 text-left">
            <th className="p-2.5 label-tiny">Concepto</th>
            <th className="p-2.5 label-tiny text-center">Visible Aguinaldo</th>
            <th className="p-2.5 label-tiny text-center">Editable</th>
            <th className="p-2.5 label-tiny text-center">Visible PDF</th>
          </tr></thead>
          <tbody>
            {S6_ITEMS.map(([k, label]) => (
              <tr key={k} className="border-t border-border">
                <td className="p-2.5 text-slate-700">{label}</td>
                <td className="p-2.5 text-center"><div className="flex justify-center"><Switch checked={c(k).visibleCalc} onCheckedChange={(v) => setFlag(k, "visibleCalc", v)} data-testid={`s6-${k}-calc`} /></div></td>
                <td className="p-2.5 text-center"><div className="flex justify-center"><Switch checked={c(k).editable} onCheckedChange={(v) => setFlag(k, "editable", v)} data-testid={`s6-${k}-editable`} /></div></td>
                <td className="p-2.5 text-center"><div className="flex justify-center"><Switch checked={c(k).visiblePdf} onCheckedChange={(v) => setFlag(k, "visiblePdf", v)} data-testid={`s6-${k}-pdf`} /></div></td>
              </tr>
            ))}
            <tr className="border-t border-border bg-slate-50">
              <td className="p-2.5 text-slate-700">
                Ítems adicionales (agregados por el usuario)
                <span className="block text-[0.7rem] text-muted-foreground font-normal mt-0.5">Filas que el usuario agrega en la calculadora. Siempre visibles y editables.</span>
              </td>
              <td className="p-2.5 text-center"><div className="flex justify-center"><Switch checked disabled data-testid="s6-extra-calc" /></div></td>
              <td className="p-2.5 text-center"><div className="flex justify-center"><Switch checked disabled data-testid="s6-extra-editable" /></div></td>
              <td className="p-2.5 text-center"><div className="flex justify-center"><Switch checked={extraPdf} onCheckedChange={setExtraPdf} data-testid="s6-extra-pdf" /></div></td>
            </tr>
          </tbody>
        </table>

        {/* Simulador */}
        <div className="space-y-4" data-testid="s6-simulator">
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-1">
              <span className="label-tiny mr-1">Tier:</span>
              {["18", "20.40"].map((t) => (
                <button key={t} type="button" onClick={() => setTier(t)} data-testid={`s6-tier-${t}`}
                  className={`px-2.5 py-1 text-xs border ${tier === t ? "brand-bg text-white border-transparent" : "border-border text-slate-600 bg-white"}`}>
                  {t === "18" ? "18%" : "20,40%"}
                </button>
              ))}
            </div>
            <button type="button" onClick={loadParams} data-testid="s6-refresh" className="text-xs text-slate-500 hover:brand-text underline">Actualizar tasas</button>
          </div>
          <div className="border border-border">
            <div className="px-3 py-2 brand-bg"><span className="label-tiny label-on-dark">Vista previa — Aguinaldo (calculadora)</span></div>
            <table className="w-full text-xs">
              <tbody>
                {S6_ITEMS.map(([k, label]) => c(k).visibleCalc ? (
                  <tr key={k} className="border-t border-border text-slate-600">
                    <td className="p-2">{label.split(" (")[0]}</td>
                    <td className="p-2 text-right tabular" data-testid={`s6-sim-rate-${k}`}>{fmtPct(rate(k))} {c(k).editable ? "✎" : "🔒"}</td>
                    <td className="p-2 text-right tabular">{fmtMon(imp(k))}</td>
                  </tr>
                ) : null)}
                <tr className="border-t border-border bg-slate-50 font-bold text-slate-900"><td className="p-2">COSTO TOTAL DEL SAC</td><td /><td className="p-2 text-right tabular" data-testid="s6-sim-costototal">{fmtMon(costoTotal)}</td></tr>
              </tbody>
            </table>
          </div>
          <div className="border border-border">
            <div className="px-3 py-2 bg-slate-800"><span className="label-tiny label-on-dark">Vista previa — Recibo (PDF)</span></div>
            <table className="w-full text-xs">
              <tbody>
                {sussVisiblePdf && <tr className="border-t border-border text-slate-600"><td className="p-2">Contribución Jubilación / SIPA</td><td className="p-2 text-right tabular">{fmtMon(sussImp)}</td></tr>}
                {c("osecac").visiblePdf && <tr className="border-t border-border text-slate-600"><td className="p-2">Contribución Obra Social</td><td className="p-2 text-right tabular">{fmtMon(imp("osecac"))}</td></tr>}
                {c("art").visiblePdf && <tr className="border-t border-border text-slate-600"><td className="p-2">A.R.T. (Riesgos del Trabajo)</td><td className="p-2 text-right tabular">{fmtMon(imp("art"))}</td></tr>}
                {c("scvo").visiblePdf && <tr className="border-t border-border text-slate-600"><td className="p-2">S.C.V.O. (Seguro de vida obligatorio)</td><td className="p-2 text-right tabular">{fmtMon(imp("scvo"))}</td></tr>}
                <tr className="border-t border-border bg-slate-50 font-bold text-slate-900"><td className="p-2">SUB TOTAL CONTRIBUCIONES</td><td className="p-2 text-right tabular" data-testid="s6-sim-subtotal">{fmtMon(subtotal)}</td></tr>
              </tbody>
            </table>
          </div>
          <p className="text-[0.7rem] text-muted-foreground">Las alícuotas e importes se toman de <strong>Parámetros vigentes</strong> (tier seleccionado). Si editás Parámetros, guardá y tocá "Actualizar tasas". Importes con bases de ejemplo. ✎ editable · 🔒 bloqueado. En el PDF, SIPA/PAMI/FNE/AF se unifican en una sola línea.</p>
        </div>
      </div>
    </Panel>
  );
}


function LandingField({ label, value, onChange, area, testId }) {
  return (
    <div>
      <Label className="label-tiny">{label}</Label>
      {area
        ? <Textarea data-testid={testId} className="mt-1.5 rounded-none" rows={3} value={value || ""} onChange={(e) => onChange(e.target.value)} />
        : <Input data-testid={testId} className="mt-1.5 rounded-none" value={value || ""} onChange={(e) => onChange(e.target.value)} />}
    </div>
  );
}

function LandingSection({ title, children }) {
  return (
    <div className="border border-border p-5">
      <p className="label-tiny mb-3" style={{ color: "#00C8C8" }}>{title}</p>
      <div className="space-y-3">{children}</div>
    </div>
  );
}

function LandingTab() {
  const [c, setC] = useState(null);
  useEffect(() => { api.get("/admin/landing-content").then((r) => setC(r.data)).catch(() => {}); }, []);
  if (!c) return null;
  const setSec = (sec, field, v) => setC({ ...c, [sec]: { ...c[sec], [field]: v } });
  const setList = (sec, field, idx, key, v) => {
    const arr = [...c[sec][field]]; arr[idx] = key ? { ...arr[idx], [key]: v } : v;
    setC({ ...c, [sec]: { ...c[sec], [field]: arr } });
  };
  const setTrust = (idx, v) => { const arr = [...c.trust]; arr[idx] = v; setC({ ...c, trust: arr }); };
  const save = async () => {
    try {
      const r = await api.put("/admin/landing-content", c);
      if (r.data?.ssrWarning) toast.warning(r.data.ssrWarning);
      else toast.success("Contenido del landing actualizado (página pública regenerada)");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const Section = LandingSection;

  return (
    <Panel title="Landing — Contenido" actions={<div className="flex gap-2"><a href="/" target="_blank" rel="noreferrer" className="text-xs underline text-slate-500 self-center">Ver landing ↗</a><Button onClick={save} data-testid="landing-save" className="rounded-none brand-bg text-white gap-2"><Save size={15} /> Guardar</Button></div>}>
      <p className="text-xs text-muted-foreground mb-4">Editá todos los textos del landing. El encabezado, el pie de página y la animación se configuran aparte (Branding) y no se modifican acá. Los íconos son fijos.</p>
      <div className="space-y-5" data-testid="landing-form">
        <Section title="Hero">
          <LandingField label="Insignia (badge)" value={c.hero.badge} onChange={(v) => setSec("hero", "badge", v)} />
          <LandingField label="Título — línea principal" value={c.hero.titleLine1} onChange={(v) => setSec("hero", "titleLine1", v)} />
          <LandingField label="Título — parte destacada (color)" value={c.hero.titleHighlight} onChange={(v) => setSec("hero", "titleHighlight", v)} />
          <LandingField label="Subtítulo" area value={c.hero.subtitle} onChange={(v) => setSec("hero", "subtitle", v)} />
          <div className="grid grid-cols-2 gap-3">
            <LandingField label="Botón (texto)" value={c.hero.ctaText} onChange={(v) => setSec("hero", "ctaText", v)} />
            <LandingField label="Nota bajo el botón" value={c.hero.ctaNote} onChange={(v) => setSec("hero", "ctaNote", v)} />
          </div>
        </Section>

        <Section title="Franja de confianza (4 ítems)">
          <div className="grid grid-cols-2 gap-3">
            {c.trust.map((t, i) => <LandingField key={i} label={`Ítem ${i + 1}`} value={t} onChange={(v) => setTrust(i, v)} />)}
          </div>
        </Section>

        <Section title="Características">
          <LandingField label="Etiqueta (eyebrow)" value={c.features.eyebrow} onChange={(v) => setSec("features", "eyebrow", v)} />
          <LandingField label="Título de la sección" value={c.features.title} onChange={(v) => setSec("features", "title", v)} />
          {c.features.cards.map((card, i) => (
            <div key={i} className="grid grid-cols-2 gap-3 border-t border-border pt-3">
              <LandingField label={`Tarjeta ${i + 1} — Título`} value={card.title} onChange={(v) => setList("features", "cards", i, "title", v)} />
              <LandingField label={`Tarjeta ${i + 1} — Descripción`} area value={card.desc} onChange={(v) => setList("features", "cards", i, "desc", v)} />
            </div>
          ))}
        </Section>

        <Section title="Cómo funciona">
          <LandingField label="Etiqueta (eyebrow)" value={c.howItWorks.eyebrow} onChange={(v) => setSec("howItWorks", "eyebrow", v)} />
          <LandingField label="Título de la sección" value={c.howItWorks.title} onChange={(v) => setSec("howItWorks", "title", v)} />
          {c.howItWorks.steps.map((s, i) => (
            <div key={i} className="grid grid-cols-2 gap-3 border-t border-border pt-3">
              <LandingField label={`Paso ${i + 1} — Título`} value={s.title} onChange={(v) => setList("howItWorks", "steps", i, "title", v)} />
              <LandingField label={`Paso ${i + 1} — Descripción`} area value={s.desc} onChange={(v) => setList("howItWorks", "steps", i, "desc", v)} />
            </div>
          ))}
        </Section>

        <Section title="Por qué Holistor">
          <LandingField label="Etiqueta (eyebrow)" value={c.whyFree.eyebrow} onChange={(v) => setSec("whyFree", "eyebrow", v)} />
          <LandingField label="Título" value={c.whyFree.title} onChange={(v) => setSec("whyFree", "title", v)} />
          <LandingField label="Párrafo" area value={c.whyFree.paragraph} onChange={(v) => setSec("whyFree", "paragraph", v)} />
          {c.whyFree.bullets.map((b, i) => <LandingField key={i} label={`Bullet ${i + 1}`} value={b} onChange={(v) => setList("whyFree", "bullets", i, null, v)} />)}
          <div className="grid grid-cols-2 gap-3 border-t border-border pt-3">
            <LandingField label="Tarjeta — Monto" value={c.whyFree.priceBig} onChange={(v) => setSec("whyFree", "priceBig", v)} />
            <LandingField label="Tarjeta — Subtítulo" value={c.whyFree.priceSub} onChange={(v) => setSec("whyFree", "priceSub", v)} />
          </div>
          <LandingField label="Tarjeta — Párrafo" area value={c.whyFree.priceParagraph} onChange={(v) => setSec("whyFree", "priceParagraph", v)} />
          <LandingField label="Tarjeta — Botón (texto)" value={c.whyFree.priceCta} onChange={(v) => setSec("whyFree", "priceCta", v)} />
        </Section>

        <Section title="CTA final">
          <LandingField label="Título" value={c.finalCta.title} onChange={(v) => setSec("finalCta", "title", v)} />
          <LandingField label="Párrafo" area value={c.finalCta.paragraph} onChange={(v) => setSec("finalCta", "paragraph", v)} />
          <LandingField label="Botón (texto)" value={c.finalCta.ctaText} onChange={(v) => setSec("finalCta", "ctaText", v)} />
        </Section>

        <Section title="Preguntas frecuentes (SEO / IA)">
          <p className="text-xs text-muted-foreground">Estas preguntas se muestran en el landing y se publican como datos estructurados (FAQPage) para Google y los motores de IA.</p>
          <LandingField label="Etiqueta (eyebrow)" value={c.faq?.eyebrow || ""} onChange={(v) => setSec("faq", "eyebrow", v)} />
          <LandingField label="Título de la sección" value={c.faq?.title || ""} onChange={(v) => setSec("faq", "title", v)} />
          {(c.faq?.items || []).map((it, i) => (
            <div key={i} className="flex gap-2 items-start border-t border-border pt-3">
              <div className="flex-1 space-y-2">
                <LandingField label={`Pregunta ${i + 1}`} testId={`faq-q-${i}`} value={it.q} onChange={(v) => setList("faq", "items", i, "q", v)} />
                <LandingField label={`Respuesta ${i + 1}`} testId={`faq-a-${i}`} area value={it.a} onChange={(v) => setList("faq", "items", i, "a", v)} />
              </div>
              <Button size="icon" variant="ghost" data-testid={`faq-del-${i}`} className="rounded-none text-destructive h-8 w-8 mt-6"
                onClick={() => setC({ ...c, faq: { ...c.faq, items: c.faq.items.filter((_, j) => j !== i) } })}>
                <Trash2 size={15} />
              </Button>
            </div>
          ))}
          <Button variant="outline" data-testid="faq-add" className="rounded-none gap-2"
            onClick={() => setC({ ...c, faq: { ...(c.faq || {}), items: [...(c.faq?.items || []), { q: "", a: "" }] } })}>
            <Plus size={15} /> Agregar pregunta
          </Button>
        </Section>
      </div>
    </Panel>
  );
}



function LegisTab() {
  const [items, setItems] = useState([]);
  const [n, setN] = useState({ titulo: "", contenido: "", url: "" });
  const load = () => api.get("/legislacion").then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const add = async () => {
    if (!n.titulo) return toast.error("Título requerido");
    try { await api.post("/admin/legislacion", n); setN({ titulo: "", contenido: "", url: "" }); load(); toast.success("Agregado"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const del = async (id) => { await api.delete(`/admin/legislacion/${id}`); load(); toast.success("Eliminado"); };
  return (
    <div className="space-y-6">
      <Panel title="Nuevo contenido" actions={<Button onClick={add} data-testid="legis-add" className="rounded-none brand-bg text-white gap-2"><Plus size={15} /> Agregar</Button>}>
        <div className="space-y-3">
          <Input placeholder="Título" data-testid="legis-titulo" className="rounded-none" value={n.titulo} onChange={(e) => setN({ ...n, titulo: e.target.value })} />
          <Textarea placeholder="Contenido" className="rounded-none" value={n.contenido} onChange={(e) => setN({ ...n, contenido: e.target.value })} />
          <Input placeholder="URL (opcional)" className="rounded-none" value={n.url} onChange={(e) => setN({ ...n, url: e.target.value })} />
        </div>
      </Panel>
      <Panel title="Contenidos">
        <div className="space-y-2">
          {items.map((it) => (
            <div key={it.id} className="flex justify-between items-start gap-4 py-2 border-b border-border last:border-0">
              <div><p className="font-semibold text-slate-900 text-sm">{it.titulo}</p><p className="text-xs text-muted-foreground line-clamp-2">{it.contenido}</p></div>
              <Button size="icon" variant="ghost" data-testid={`legis-del-${it.id}`} className="rounded-none text-destructive h-8 w-8" onClick={() => del(it.id)}><Trash2 size={15} /></Button>
            </div>
          ))}
        </div>
      </Panel>
    </div>
  );
}

const QTYPES = [["single", "Opción única"], ["multi", "Opción múltiple"], ["text", "Texto libre"]];
const newQid = () => `q${Date.now().toString(36)}${Math.floor(Math.random() * 1000)}`;

function FeedbackTab() {
  const [f, setF] = useState(null);
  const [stats, setStats] = useState(null);
  const [responses, setResponses] = useState([]);

  const loadAll = () => {
    api.get("/admin/feedback-form").then((r) => setF(r.data)).catch(() => {});
    api.get("/admin/feedback-stats").then((r) => setStats(r.data)).catch(() => {});
    api.get("/admin/feedback-responses").then((r) => setResponses(r.data)).catch(() => {});
  };
  useEffect(() => { loadAll(); }, []);
  if (!f) return null;

  const setQ = (i, patch) => setF((p) => { const qs = [...p.questions]; qs[i] = { ...qs[i], ...patch }; return { ...p, questions: qs }; });
  const setOpt = (i, oi, v) => setQ(i, { options: f.questions[i].options.map((o, j) => (j === oi ? v : o)) });
  const addOpt = (i) => setQ(i, { options: [...(f.questions[i].options || []), ""] });
  const delOpt = (i, oi) => setQ(i, { options: f.questions[i].options.filter((_, j) => j !== oi) });
  const addQ = () => {
    if ((f.questions || []).length >= 5) { toast.error("Máximo 5 preguntas"); return; }
    setF((p) => ({ ...p, questions: [...p.questions, { id: newQid(), label: "", type: "single", options: ["", ""], required: true }] }));
  };
  const delQ = (i) => setF((p) => ({ ...p, questions: p.questions.filter((_, j) => j !== i) }));

  const save = async () => {
    for (const q of f.questions) {
      if (!q.label.trim()) { toast.error("Cada pregunta necesita un enunciado"); return; }
      if (q.type !== "text" && q.options.filter((o) => o.trim()).length < 2) { toast.error(`"${q.label || "Pregunta"}" necesita al menos 2 opciones`); return; }
    }
    try {
      await api.put("/admin/feedback-form", {
        enabled: f.enabled, title: f.title, description: f.description,
        questions: f.questions.map((q) => ({ id: q.id, label: q.label, type: q.type, options: q.type === "text" ? [] : q.options, required: q.required })),
      });
      toast.success("Formulario de feedback guardado"); loadAll();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-6" data-testid="feedback-tab">
      {/* Report */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-border border border-border">
          {[["Formularios mostrados", stats.prompted], ["Respuestas recibidas", stats.submitted], ["Tasa de respuesta", `${stats.rate}%`], ["Total respuestas", stats.totalResponses]].map(([l, v]) => (
            <div key={l} className="bg-white p-5" data-testid={`fb-stat-${l}`}><p className="label-tiny">{l}</p><p className="font-head text-3xl font-bold mt-1 tabular">{v}</p></div>
          ))}
        </div>
      )}

      {/* Builder */}
      <Panel title="Formulario de feedback (obligatorio antes del primer recibo)" actions={
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2"><span className="label-tiny">Activo</span><Switch checked={f.enabled} onCheckedChange={(v) => setF({ ...f, enabled: v })} data-testid="fb-enabled" /></div>
          <Button onClick={save} data-testid="fb-save" className="rounded-none brand-bg text-white gap-2"><Save size={15} /> Guardar</Button>
        </div>}>
        <p className="text-xs text-muted-foreground mb-4">Cuando está activo, cada usuario debe completar este formulario una sola vez, antes de ver/descargar/imprimir su primer recibo. Los administradores no lo ven.</p>
        <div className="grid md:grid-cols-2 gap-4 mb-5">
          <div><Label className="label-tiny">Título</Label><Input data-testid="fb-title" className="mt-1.5 rounded-none" value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} /></div>
          <div><Label className="label-tiny">Descripción</Label><Input data-testid="fb-desc" className="mt-1.5 rounded-none" value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} /></div>
        </div>

        <div className="space-y-4">
          {f.questions.map((q, i) => (
            <div key={q.id} className="border border-border p-4" data-testid={`fb-question-${i}`}>
              <div className="flex items-start gap-3">
                <div className="flex-1 space-y-3">
                  <Input placeholder="Enunciado de la pregunta" data-testid={`fb-q-label-${i}`} className="rounded-none" value={q.label} onChange={(e) => setQ(i, { label: e.target.value })} />
                  <div className="flex flex-wrap items-center gap-4">
                    <div className="w-48">
                      <Select value={q.type} onValueChange={(v) => setQ(i, { type: v, options: v === "text" ? [] : (q.options.length ? q.options : ["", ""]) })}>
                        <SelectTrigger className="rounded-none" data-testid={`fb-q-type-${i}`}><SelectValue /></SelectTrigger>
                        <SelectContent>{QTYPES.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <label className="flex items-center gap-2 text-sm text-slate-600"><Switch checked={q.required} onCheckedChange={(v) => setQ(i, { required: v })} data-testid={`fb-q-required-${i}`} /> Obligatoria</label>
                  </div>
                  {q.type !== "text" && (
                    <div className="space-y-2 pl-1">
                      {q.options.map((o, oi) => (
                        <div key={oi} className="flex items-center gap-2">
                          <Input placeholder={`Opción ${oi + 1}`} data-testid={`fb-q-${i}-opt-${oi}`} className="rounded-none h-8" value={o} onChange={(e) => setOpt(i, oi, e.target.value)} />
                          <button type="button" onClick={() => delOpt(i, oi)} className="text-destructive p-1" data-testid={`fb-q-${i}-opt-del-${oi}`}><Trash2 size={14} /></button>
                        </div>
                      ))}
                      <button type="button" onClick={() => addOpt(i)} className="text-xs brand-text underline" data-testid={`fb-q-${i}-add-opt`}>+ Agregar opción</button>
                    </div>
                  )}
                </div>
                <Button size="icon" variant="ghost" className="rounded-none text-destructive h-8 w-8" onClick={() => delQ(i)} data-testid={`fb-q-del-${i}`}><Trash2 size={15} /></Button>
              </div>
            </div>
          ))}
          <Button onClick={addQ} variant="outline" className="rounded-none gap-2" data-testid="fb-add-question"><Plus size={15} /> Agregar pregunta</Button>
        </div>
      </Panel>

      {/* Per-question breakdown */}
      {stats?.perQuestion?.length > 0 && (
        <Panel title="Resumen de respuestas">
          <div className="space-y-4">
            {stats.perQuestion.map((q) => (
              <div key={q.id} className="border-b border-border pb-3 last:border-0">
                <p className="text-sm font-medium text-slate-800 mb-2">{q.label}</p>
                {q.type === "text" ? (
                  <p className="text-xs text-muted-foreground">{q.textCount} respuesta(s) de texto — ver tabla abajo.</p>
                ) : (
                  <div className="space-y-1">
                    {Object.entries(q.options || {}).map(([opt, cnt]) => (
                      <div key={opt} className="flex justify-between text-sm"><span className="text-slate-600">{opt}</span><span className="tabular font-medium">{cnt}</span></div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </Panel>
      )}

      {/* Responses table */}
      <Panel title="Respuestas individuales">
        {responses.length === 0 ? (
          <p className="text-sm text-muted-foreground">Aún no hay respuestas.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="fb-responses-table">
              <thead><tr className="border-b border-border text-left">{["Usuario", "Email", "Respuestas", "Fecha"].map((h) => <th key={h} className="p-2.5 label-tiny">{h}</th>)}</tr></thead>
              <tbody>
                {responses.map((r) => (
                  <tr key={r.id} className="border-b border-border last:border-0 align-top">
                    <td className="p-2.5 font-medium text-slate-900">{r.userNombre || "-"}</td>
                    <td className="p-2.5">{r.userEmail}</td>
                    <td className="p-2.5">
                      <div className="space-y-1">
                        {(r.answers || []).map((a, ai) => (
                          <div key={ai} className="text-xs"><span className="text-slate-500">{a.label}:</span> <span className="text-slate-800">{Array.isArray(a.value) ? a.value.join(", ") : String(a.value || "—")}</span></div>
                        ))}
                      </div>
                    </td>
                    <td className="p-2.5 font-mono text-xs whitespace-nowrap">{(r.submittedAt || "").slice(0, 10)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Panel>
    </div>
  );
}

function LeadsTab() {
  const [leads, setLeads] = useState([]);
  const [usage, setUsage] = useState(null);
  const [tycCfg, setTycCfg] = useState(null);
  const [page, setPage] = useState(1);
  const PER_PAGE = 30;
  useEffect(() => { api.get("/admin/leads").then((r) => setLeads(r.data)).catch(() => {}); api.get("/admin/usage").then((r) => setUsage(r.data)).catch(() => {}); api.get("/admin/tyc").then((r) => setTycCfg(r.data)).catch(() => {}); }, []);
  const fShort = (iso) => (iso ? `${iso.slice(8, 10)}/${iso.slice(5, 7)}/${iso.slice(2, 4)}` : "");
  const tycBadge = (l) => {
    if (!tycCfg?.enabled || !tycCfg?.currentVersion) return <span className="text-slate-400">—</span>;
    const cur = tycCfg.currentVersion;
    if (l.tycAcceptedVersion === cur)
      return <span className="inline-block px-1.5 py-0.5 text-xs bg-emerald-50 text-emerald-700 border border-emerald-200 whitespace-nowrap">✔ Sí (v{cur} · {fShort(l.tycAcceptedAt)})</span>;
    if (l.tycRejectedVersion === cur)
      return <span className="inline-block px-1.5 py-0.5 text-xs bg-red-50 text-red-700 border border-red-200 whitespace-nowrap">Rechazó ({fShort(l.tycRejectedAt)})</span>;
    if (l.tycAcceptedVersion)
      return <span className="inline-block px-1.5 py-0.5 text-xs bg-slate-100 text-slate-500 whitespace-nowrap">No (aceptó v{l.tycAcceptedVersion})</span>;
    return <span className="inline-block px-1.5 py-0.5 text-xs bg-slate-100 text-slate-500">No</span>;
  };
  const totalPages = Math.max(1, Math.ceil(leads.length / PER_PAGE));
  const curPage = Math.min(page, totalPages);
  const start = (curPage - 1) * PER_PAGE;
  const pageLeads = leads.slice(start, start + PER_PAGE);
  const exportCsv = async (path, name) => {
    try {
      const { data } = await api.get(path, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([data]));
      const a = document.createElement("a"); a.href = url; a.download = name; a.click();
      toast.success("Exportado");
    } catch { toast.error("Error al exportar"); }
  };
  return (
    <div className="space-y-6">
      {usage && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-border border border-border">
          {[
            ["Leads", usage.totalLeads], ["Cálculos", usage.totalCalculos], ["Logins", usage.logins],
            ["Recibos generados", usage.recibosGenerados], ["Recibos descargados", usage.recibosDescargados],
            ["Recibos impresos", usage.recibosImpresos],
          ].map(([l, v]) => (
            <div key={l} className="bg-white p-5" data-testid={`usage-${l}`}><p className="label-tiny">{l}</p><p className="font-head text-3xl font-bold mt-1 tabular">{v}</p></div>
          ))}
        </div>
      )}
      <Panel title="Leads capturados" actions={
        <div className="flex gap-2">
          <Button onClick={() => exportCsv("/admin/usage/export", "uso.csv")} variant="outline" data-testid="export-usage" className="rounded-none gap-2"><Download size={15} /> Uso CSV</Button>
          <Button onClick={() => exportCsv("/admin/leads/export", "leads.csv")} data-testid="export-leads" className="rounded-none brand-bg text-white gap-2"><Download size={15} /> Leads CSV</Button>
        </div>}>
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[920px]" data-testid="leads-table">
            <thead><tr className="border-b border-border text-left">
              {["Nombre", "Email", "Teléfono", "Cliente", "T&C"].map((h) => <th key={h} className="p-2.5 label-tiny">{h}</th>)}
              {["Cálculos", "Logins", "Rec. gen.", "Rec. desc.", "Rec. impr."].map((h) => <th key={h} className="p-2.5 label-tiny text-right whitespace-nowrap">{h}</th>)}
              <th className="p-2.5 label-tiny">Alta</th>
            </tr></thead>
            <tbody>
              {pageLeads.map((l) => (
                <tr key={l.id} className="border-b border-border last:border-0">
                  <td className="p-2.5 font-medium text-slate-900">{l.nombre}</td>
                  <td className="p-2.5">{l.email}</td>
                  <td className="p-2.5">{l.telefono}</td>
                  <td className="p-2.5">{l.esClienteHolistor ? "Sí" : "No"}</td>
                  <td className="p-2.5" data-testid={`lead-tyc-${l.id}`}>{tycBadge(l)}</td>
                  <td className="p-2.5 text-right tabular" data-testid={`lead-calculos-${l.id}`}>{l.calculos ?? 0}</td>
                  <td className="p-2.5 text-right tabular" data-testid={`lead-logins-${l.id}`}>{l.logins ?? 0}</td>
                  <td className="p-2.5 text-right tabular" data-testid={`lead-recgen-${l.id}`}>{l.recibosGenerados ?? 0}</td>
                  <td className="p-2.5 text-right tabular" data-testid={`lead-recdesc-${l.id}`}>{l.recibosDescargados ?? 0}</td>
                  <td className="p-2.5 text-right tabular" data-testid={`lead-recimpr-${l.id}`}>{l.recibosImpresos ?? 0}</td>
                  <td className="p-2.5 font-mono text-xs whitespace-nowrap">{(l.created_at || "").slice(0, 10)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {leads.length > 0 && (
          <div className="flex items-center justify-between mt-4 pt-3 border-t border-border" data-testid="leads-pagination">
            <span className="text-xs text-muted-foreground">
              Mostrando {start + 1}–{Math.min(start + PER_PAGE, leads.length)} de {leads.length}
            </span>
            <div className="flex items-center gap-3">
              <Button variant="outline" className="rounded-none h-8 px-3" disabled={curPage <= 1} onClick={() => setPage(curPage - 1)} data-testid="leads-page-prev">‹ Anterior</Button>
              <span className="text-xs text-slate-600" data-testid="leads-page-indicator">Página {curPage} de {totalPages}</span>
              <Button variant="outline" className="rounded-none h-8 px-3" disabled={curPage >= totalPages} onClick={() => setPage(curPage + 1)} data-testid="leads-page-next">Siguiente ›</Button>
            </div>
          </div>
        )}
      </Panel>
    </div>
  );
}
